import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Mascot from "@/components/Mascot";
import { CHALLENGE_TEMPLATES } from "@/data/parentingData";
import { minAgeOf } from "@/lib/cheers";

const weekIndex = () => Math.floor(Date.now() / 604800000);

export default function WeeklyChallenge({ child, activeTitles = [], onLaunched }) {
  const [busy, setBusy] = useState(false);

  const pick = useMemo(() => {
    const age = child?.age;
    const pool =
      typeof age === "number"
        ? CHALLENGE_TEMPLATES.filter((t) => minAgeOf(t.ages) <= age)
        : CHALLENGE_TEMPLATES;
    const list = pool.length ? pool : CHALLENGE_TEMPLATES;
    return list[weekIndex() % list.length];
  }, [child?.age]);

  const alreadyRunning = activeTitles.includes(pick.title);

  const launch = async () => {
    setBusy(true);
    try {
      await api.post("/challenges", {
        title: pick.title,
        description: pick.description,
        duration_days: pick.duration_days,
      });
      toast.success("Défi de la semaine lancé 💪");
      onLaunched?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de lancer ce défi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="weekly-challenge-card"
      className="soft-card relative overflow-hidden p-6 sm:p-8 !bg-[#D2EDE1]"
    >
      <div className="absolute right-2 -bottom-3 hidden sm:block">
        <Mascot pose="trophy" size={130} anim="wiggle" delay={200} />
      </div>
      <div className="relative z-10 sm:pr-40">
        <div className="flex items-center gap-2 text-[#16233D] text-xs font-bold uppercase tracking-wider">
          <Trophy className="h-4 w-4" /> Défi de la semaine
        </div>
        <h3 className="mt-2 text-2xl font-semibold text-[#16233D]" data-testid="weekly-challenge-title">
          {pick.title}
        </h3>
        <p className="mt-2 text-[#23375E] leading-relaxed">{pick.description}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="age-badge bg-white/70">{pick.duration_days} jour{pick.duration_days > 1 ? "s" : ""}</span>
          <span className="age-badge bg-white/70">{pick.category}</span>
          <span className="age-badge bg-white/70">{pick.ages}</span>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {alreadyRunning ? (
            <span
              data-testid="weekly-challenge-running"
              className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#16233D]"
            >
              Déjà en cours 🎯
            </span>
          ) : (
            <button
              data-testid="weekly-challenge-launch"
              onClick={launch}
              disabled={busy}
              className="brand-btn rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />}
              {busy ? "On lance…" : "Relever ce défi"}
            </button>
          )}
          <Link
            to="/defis"
            data-testid="weekly-challenge-all"
            className="inline-flex items-center gap-1 text-sm font-medium text-[#23375E] hover:underline"
          >
            Tous les défis <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
